import {
  createRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  useRef,
} from "react";
import { Box, Button, Modal, Paper } from "@material-ui/core";
import { useStyles } from "../../../Styles/styles";
import { reducer } from "../Reducer/sizeAndWeightReducer";
import { checkPermission, shouldBeLocked } from "../../../utils/Roles";
import styles from "./SizeAndWeightStyles.module.css";
import Dimensions from "./Dimensions";
import BridgeFormulaDistance from "./BridgeFormulaDistance";
import AxleGroupWeightsSection from "./AxleGroupWeightsSection/AxleGroupWeightSection/AxleGroupWeightsSection";
import OutcomeSection from "./OutcomeSection";
import { useToggle } from "../../../CustomHooks/useToggle";
import CloseIcon from "@material-ui/icons/Close";
import clsx from "clsx";
import InspectionSection from "./InspectionSection";
import InvolvedPersons from "./InvolvedPersons";
import "./WeightSlip/WeightSlip";
import { addOrUpdateSizeAndWeightInIncidentInputs } from "../utils/stateNameLookUp";
import { useMutation } from "@apollo/client";
import { ADD_OR_UPDATE_SIZE_AND_WEIGHT_IN_INCIDENT } from "../Mutations/AddOrUpdateSizeAndWeightInIncident";
import {
  convertMomentTimeToDateString,
  parseDateFlexible,
  parseObjectArrayIdString,
} from "../../../utils/utils";
import moment from "moment";
import {
  calculateAxleTotals,
  findMostOverLimitAxleGroup,
  findWeightRestrictionViolation,
  returnPlatformGrossWeight,
} from "../utils/utils";
import { validateState } from "../utils/printValidation";
import PowerUnitAndTrailers from "./PowerUnitAndTrailers";
import { IncidentsContext } from "../../../Contexts/IncidentsContext";
import { INCIDENT_ACTIONS } from "../../../Actions/IncidentActions";

// Inside your component

const SizeAndWeightFrame = ({
  incidentNumber,
  people = [],
  piss,
  viss,
  incident,
  userRoles,
  userId,
  editableOriIds = [],
  incidentTickets,
  incidentOfficers,
  primaryOfficer,
  setPausePolling,
  nightMode = false,
  ...props
}) => {
  const componentRef = createRef();
  //locking logic
  //TODO: add the sizeAndWeight.locked attribute to memoizedLock

  const key = Object.keys(props?.data)?.[0] || null;
  //this will handle the data being fetched by getSizeAndWeight or created by addOrUpdateSizeAndWeightInIncident
  const sizeAndWeightObj =
    props?.data?.[key]?.sizeAndWeight || props?.data?.[key] || null;

  const { incidentDispatch } = useContext(IncidentsContext);
  const { UPDATE_INCIDENT } = INCIDENT_ACTIONS;

  const [state, dispatch] = useReducer(reducer, sizeAndWeightObj);
  const [showPrintModal, togglePrintModal] = useToggle(false);
  const [missingFields, setMissingFields] = useState([]);

  const [addOrUpdateSizeAndWeightInIncident] = useMutation(
    ADD_OR_UPDATE_SIZE_AND_WEIGHT_IN_INCIDENT,
    {
      context: { serializationKey: "incidentQueue" },
    }
  );

  const {
    operator,
    operatorAddress,
    operatorPhone,
    carrier,
    carrierAddress,
    carrierPhone,
    owner,
    ownerAddress,
    ownerPhone,
    height,
    width,
    length,
    permitNumberFiled,
    steerAxleTireSize,
    id,
    innerDimension,
    outerDimension,
    triaxleDimension,
    roadLimitWeight,
    axleLimitWeight,
    registeredWeight,
    violationIdentified,
    courtAction,
    offloadRedistribution,
    issuedTo,
    endedAt,
    platformWeigh,
    dimensionViolationIdentified,
    powerUnit,
    printCarrier,
    printOwner,
    grossWeight,
    platformOverUnder,
    roadLimitOverUnder,
    steerAxleLimit,
    locked,
  } = state || {};

  const { commodities, axleGroups, scales, trailers } = state || [];

  const memoizedLocked = useMemo(() => {
    return shouldBeLocked(false, props?.canEdit, userRoles) || !!locked;
  }, [locked, props?.canEdit, userRoles]);

  const canUnlock = useMemo(() => {
    return checkPermission(
      userRoles,
      "unlock_sw",
      editableOriIds,
      incident?.ori?.id,
      incident?.id
    );
  }, [editableOriIds, incident?.id, incident?.ori?.id, userRoles]);

  const vehicles = useMemo(() => {
    let v = viss.map(v => v?.vehicle).filter(vehicle => !!vehicle?.id) ?? [];
    if (locked) {
      v.push(powerUnit);
      if (Array.isArray(trailers)) v.push(...trailers);
    }
    return v;
  }, [viss, locked, powerUnit, trailers]);

  const incidentPeople = useMemo(() => {
    let p = people;
    if (locked) {
      p = p.concat([operator, carrier, owner]);
    }
    return p;
  }, [carrier, locked, operator, owner, people]);

  const stateRef = useRef();

  const bridgeFormulaDistances = {
    innerDimension,
    outerDimension,
    triaxleDimension,
  };
  const dimensions = {
    height,
    width,
    length,
    permitNumberFiled,
    commodities,
    dimensionViolationIdentified,
  };
  const weightLimits = {
    roadLimitWeight,
    axleLimitWeight,
    registeredWeight,
    platformWeigh,
    steerAxleTireSize,
    grossWeight,
    platformOverUnder,
    roadLimitOverUnder,
    steerAxleLimit,
  };
  const outcome = {
    violationIdentified,
    courtAction,
    offloadRedistribution,
    issuedTo,
    endedAt,
    scales,
  };

  const platformGrossWeight = useMemo(() => {
    return returnPlatformGrossWeight(platformWeigh, axleGroups);
  }, [axleGroups, platformWeigh]);

  const axleGrossWeights = useMemo(() => {
    return calculateAxleTotals(axleGroups);
  }, [axleGroups]);

  const mostOverWeightAxleGroup = useMemo(
    () => findMostOverLimitAxleGroup(state),
    [state]
  );
  const weightViolation = useMemo(
    () => findWeightRestrictionViolation(incidentTickets),
    [incidentTickets]
  );

  const issuedToRoleName = useMemo(() => {
    const issuedToId = weightViolation?.issuedTo?.id;
    const matchingPis = piss?.find(pis => pis?.person?.id === issuedToId);
    return matchingPis?.incidentInvolvement?.name || "";
  }, [piss, weightViolation]);

  const handleChange = useCallback(
    async (val, action, payload) => {
      if (val === null || val === undefined) return;
      let value = val;
      let mutationValue = val;
      const stateName = payload.property;
      const useAddOrUpdateSizeAndWeightInIncident = Object.keys(
        addOrUpdateSizeAndWeightInIncidentInputs
      ).includes(stateName);

      payload.value = value;
      let localTimeString = null;
      if (moment.isMoment(value)) {
        let parsedDate = parseDateFlexible(value);
        if (parsedDate?.isValid()) {
          localTimeString = parsedDate.utc().format("YYYY-MM-DD HH:mm:ss UTC");
        }
        mutationValue = localTimeString;
        payload.value = localTimeString;
      }
      dispatch({ type: action, payload });

      // Mutation logic
      if (stateName && useAddOrUpdateSizeAndWeightInIncident) {
        const mutationInformation =
          addOrUpdateSizeAndWeightInIncidentInputs[stateName];
        const { name, accessId, hasMany } = mutationInformation;
        if (hasMany && accessId) {
          mutationValue = parseObjectArrayIdString(value);
        }
        if (accessId && !hasMany) {
          mutationValue = +value?.id || "";
        }

        try {
          await addOrUpdateSizeAndWeightInIncident({
            variables: {
              input: {
                incidentId: incident?.id,
                sizeAndWeightId: id,
                originatorId: userId,
                [name]: mutationValue,
              },
            },
          });
        } catch (e) {
          console.error(e);
        }
      } else {
        alert(
          `${stateName} is not an input on addOrUpdateSizeAndWeightInIncidentInputs`
        );
      }
    },
    [addOrUpdateSizeAndWeightInIncident, id, incident?.id, userId]
  );

  const updateGrossWeight = useCallback(
    overrideWeight => {
      const calculatedGross =
        overrideWeight >= 0
          ? overrideWeight
          : platformWeigh
          ? platformGrossWeight
          : axleGrossWeights?.gross;
      if (+calculatedGross !== +grossWeight) {
        //update the gross weight with mutation addOrUpdateSizeAndWeightInIncident
        const newPlatformOverUnder = +calculatedGross - +registeredWeight;
        const newRoadLimitOverUnder = +calculatedGross - +roadLimitWeight;
        handleChange(calculatedGross, "UPDATE_PROPERTY", {
          property: "grossWeight",
        });
        handleChange(newPlatformOverUnder, "UPDATE_PROPERTY", {
          property: "platformOverUnder",
        });
        handleChange(newRoadLimitOverUnder, "UPDATE_PROPERTY", {
          property: "roadLimitOverUnder",
        });
      }
    },
    [
      platformWeigh,
      platformGrossWeight,
      axleGrossWeights?.gross,
      grossWeight,
      registeredWeight,
      roadLimitWeight,
      handleChange,
    ]
  );

  const handlePrintPreviewClick = async () => {
    const res = validateState(state, incident);
    if (res?.valid) {
      setMissingFields([]);
      togglePrintModal();
      return null;
    } else {
      const names = res?.invalidFields.map(field => field?.name).join(", ");
      highlightMissingFields(res?.invalidFields);
      alert("Please correct the following fields before printing: " + names);
      return null;
    }
  };

  function highlightMissingFields(fields) {
    setMissingFields(fields);
  }

  const updateLockedTo = async bool => {
    if (locked === bool) return true;
    if (locked && !bool) {
      if (
        window.confirm(
          "This will unlock the Size & Weight frame. Do you wish to proceed?"
        )
      ) {
        await handleChange(bool, "UPDATE_PROPERTY", { property: "locked" });
        await props.refetch();
        return true;
      } else {
        return false;
      }
    }
    if (!locked && bool) {
      if (
        window.confirm(
          "This will lock the Size & Weight frame. Do you wish to proceed?"
        )
      ) {
        await handleChange(bool, "UPDATE_PROPERTY", { property: "locked" });
        await props.refetch();
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  };

  //this will update the incident state with the sizeAndWeight endedAt time whenever it changes so it can be correctly displayed on the header
  useEffect(() => {
    let value = endedAt;
    if (moment.isMoment(endedAt)) {
      value = convertMomentTimeToDateString(endedAt);
    }
    if (
      incident?.sizeAndWeight?.endedAt !== value &&
      incident?.sizeAndWeight?.id
    ) {
      incidentDispatch({
        type: UPDATE_INCIDENT,
        payload: {
          id: incident?.id,
          accessor: "sizeAndWeight",
          property: "endedAt",
          value: value,
        },
      });
    }
  }, [UPDATE_INCIDENT, endedAt, incident, incident?.id, incidentDispatch]);

  //this keeps a ref of the current state that can be accessed in the cleanup function
  useEffect(() => {
    stateRef.current = state;
  }, [state]); // Update the ref whenever `state` changes

  //this will update the incident sizeAndWeight object when the sizeAndWeight frame is closed
  useEffect(() => {
    return () => {
      // This code will run when SizeAndWeightFrame is unmounted
      incidentDispatch({
        type: UPDATE_INCIDENT,
        payload: {
          id: incident?.id,
          property: "sizeAndWeight",
          value: stateRef.current,
        },
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array so this effect runs only on mount and unmount

  useEffect(() => {
    let val = props?.data?.[key]?.sizeAndWeight || props?.data?.[key] || null;
    if (val) {
      dispatch({ type: "REPLACE_STATE", payload: val });
    }
  }, [key, props?.data, sizeAndWeightObj]);

  return (
    <Paper
      id="size-and-weight-paper"
      className={styles["root-container"]}
      autoComplete="off"
    >
      <InspectionSection
        nightMode={nightMode}
        dispatch={dispatch}
        handleChange={handleChange}
        state={state}
        disabled={memoizedLocked}
        userRoles={userRoles}
        handleDateChange={handleChange}
        incidentId={incident?.id}
        missingFields={missingFields}
        setPausePolling={setPausePolling}
        {...props}
      />
      <PowerUnitAndTrailers
        nightMode={nightMode}
        handleChange={handleChange}
        vehicles={vehicles}
        powerUnit={powerUnit}
        trailers={trailers}
        userRoles={userRoles}
        disabled={memoizedLocked}
        missingFields={missingFields}
        locked={locked}
        {...props}
      />
      <Dimensions
        nightMode={nightMode}
        handleChange={handleChange}
        dimensions={dimensions}
        disabled={memoizedLocked}
        userRoles={userRoles}
        missingFields={missingFields}
        setPausePolling={setPausePolling}
        {...props}
      />
      <BridgeFormulaDistance
        nightMode={nightMode}
        handleChange={handleChange}
        bridgeFormulaDistances={bridgeFormulaDistances}
        dispatch={dispatch}
        disabled={memoizedLocked}
      />
      <AxleGroupWeightsSection
        nightMode={nightMode}
        vehicles={
          viss.map(v => v?.vehicle).filter(vehicle => !!vehicle?.id) ?? []
        }
        sizeAndWeightId={id}
        disabled={memoizedLocked}
        handleChange={handleChange}
        userRoles={userRoles}
        userId={userId}
        dispatch={dispatch}
        axleGroups={axleGroups}
        weightLimits={weightLimits}
        mostOverWeightAxleGroup={mostOverWeightAxleGroup}
        missingFields={missingFields}
        updateGrossWeight={updateGrossWeight}
        locked={locked}
        {...props}
      />
      <InvolvedPersons
        nightMode={nightMode}
        disabled={memoizedLocked}
        handleChange={handleChange}
        dispatch={dispatch}
        operator={operator}
        operatorAddress={operatorAddress}
        operatorPhone={operatorPhone}
        carrier={carrier}
        carrierAddress={carrierAddress}
        carrierPhone={carrierPhone}
        owner={owner}
        ownerAddress={ownerAddress}
        ownerPhone={ownerPhone}
        piss={piss}
        incidentPeople={incidentPeople}
        userRoles={userRoles}
        incidentId={incident?.id}
        printOwner={printOwner}
        printCarrier={printCarrier}
        locked={locked}
        missingFields={missingFields}
        {...props}
      />
      <OutcomeSection
        nightMode={nightMode}
        outcome={outcome}
        handleChange={handleChange}
        userRoles={userRoles}
        weightViolation={weightViolation}
        disabled={memoizedLocked}
        incidentPeople={incidentPeople}
        incidentOfficers={incidentOfficers}
        primaryOfficer={primaryOfficer}
        sizeAndWeightId={id}
        dispatch={dispatch}
        userId={userId}
        setPausePolling={setPausePolling}
        missingFields={missingFields}
        {...props}
      />
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-around"
        style={{ width: "100%", paddingTop: "3vh", paddingBottom: "3vh" }}
      >
        <Button
          disabled={false}
          variant="contained"
          onClick={handlePrintPreviewClick}
        >
          Print Preview
        </Button>
        {canUnlock && (
          <Button
            disabled={false}
            variant="contained"
            onClick={() => {
              updateLockedTo(locked ? false : true);
            }}
          >
            {locked ? "Unlock" : "Lock"}
          </Button>
        )}
      </Box>

      <PrintPreview
        nightMode={nightMode}
        componentRef={componentRef}
        showPrintModal={showPrintModal}
        togglePrintModal={togglePrintModal}
      >
        <weight-slip
          ref={componentRef}
          size-and-weight={JSON.stringify(state)}
          ticket={JSON.stringify(weightViolation)}
          primary-officer={JSON.stringify(primaryOfficer)}
          issued-to-role-name={issuedToRoleName}
          path-to-badge={"/DMVBadge.png"}
        />
      </PrintPreview>
    </Paper>
  );
};

const PrintPreview = ({
  children,
  componentRef,
  showPrintModal,
  togglePrintModal,
  nightMode = false,
}) => {
  const classes = useStyles();

  const printTicket = async () => {
    // Check if the componentRef is current and if the element exists
    const element = componentRef.current;

    if (!element) {
      console.error("No element to print");
      return;
    }

    // Create a new window
    const printWindow = window.open("", "_blank");

    // Append a clone of the element to the new window's document body
    printWindow.document.body.appendChild(element.cloneNode(true));

    // Call print on the new window, delay it slightly to ensure rendering
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  return (
    <Modal
      open={showPrintModal}
      onClose={togglePrintModal}
      className={classes.ticketModal}
      id="weight-slip-print-modal"
      keepMounted={false}
    >
      <div
        style={{
          height: "75%",
          overflow: "scroll",
          backgroundColor: nightMode ? "black" : "white",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            backgroundColor: nightMode ? "#616161" : "#fae8ad",
            borderRadius: "5px",
            padding: "0 5px",
            alignItems: "center",
          }}
        >
          <h3 style={{ color: nightMode ? "white" : "inherit" }}>
            DMV Size and Weight Violation
          </h3>
          <CloseIcon onClick={togglePrintModal} className={classes.hover} />
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            name="print"
            aria-label="print button"
            onClick={printTicket}
            className={clsx(classes.buttonHover, classes.navBarButtons)}
            data-cy="print-button"
            variant="contained"
          >
            Print
          </Button>
        </div>
        <>{children}</>
      </div>
    </Modal>
  );
};

export default SizeAndWeightFrame;
